import type { GameMode } from '../core/types/gameMode.types';
import { normalizeGameMode } from '../core/types/gameMode.types';
import type { ActivityLogicPlugin, AnyLogicPlugin, LogicPluginForMode, PluginContext, PluginMutationResult } from '../core/types/plugin.base.types';
import type { ActivityPayloadMap, ActivityRuntimeMap } from '../core/types/payloads';
import type { Participant, RoutingMode, ScorePatch } from '../core/types/room.types';
import type { HostActionPayload, StudentInputEnvelope } from '../core/types/events.types';
import { pollLogic } from '../activities/poll/poll.logic';
import { arenaLogic } from '../activities/arena/arena.logic';
import { coopLogic } from '../activities/coop/coop.logic';
import { sandboxLogic } from '../activities/sandbox/sandbox.logic';
import { sequencerLogic } from '../activities/sequencer/sequencer.logic';
import { pinpointLogic } from '../activities/pinpoint/pinpoint.logic';
import { wallLogic } from '../activities/wall/wall.logic';
import { completarLogic } from '../activities/completar/completar.logic';
import { desarrolloLogic } from '../activities/desarrollo/desarrollo.logic';
import { vinculaLogic } from '../activities/vincula/vincula.logic';
import { agrupaLogic } from '../activities/agrupa/agrupa.logic';
import { dictadoLogic } from '../activities/dictado/dictado.logic';
import { anatomiaLogic } from '../activities/anatomia/anatomia.logic';
import { rhythmCanvasLogic } from '../activities/rhythm_canvas/rhythm_canvas.logic';
import { melodyStaffLogic } from '../activities/melody_staff/melody_staff.logic';
import { earTrainingLogic } from '../activities/ear_training/ear_training.logic';
import { infoBlockLogic } from '../activities/info_block/info_block.logic';
import { canvasBreakdownLogic } from '../activities/canvas_breakdown/canvas_breakdown.logic';
import { symbolAnalysisLogic } from '../activities/symbol_analysis/symbol_analysis.logic';
import { geometryCanvasLogic } from '../activities/geometry_canvas/geometry_canvas.logic';
import { cartesianPlaneLogic } from '../activities/cartesian_plane/cartesian_plane.logic';
import { mathEquationLogic } from '../activities/math_equation/math_equation.logic';
import { numberLineLogic } from '../activities/number_line/number_line.logic';
import { countingCollectionLogic } from '../activities/counting_collection/counting_collection.logic';
import { dataChartLogic } from '../activities/data_chart/data_chart.logic';
import { fractionModelLogic } from '../activities/fraction_model/fraction_model.logic';
import { percentGridLogic } from '../activities/percent_grid/percent_grid.logic';
import {
  subtituloLogic,
  destacadoLogic,
  figuraLogic,
  glosarioTablaLogic,
  formulaDestacadaLogic,
} from '../activities/guide_editorial/guide_editorial.logic';
import { structuralBreakdownLogic } from '../activities/structural_breakdown/structural_breakdown.logic';
import { sourceAnalysisLogic } from '../activities/source_analysis/source_analysis.logic';
import { applyStimulusFields, supportsBlockStimulus } from '../core/print/blockStimulus';

const logicRegistry: Record<GameMode, AnyLogicPlugin> = {
  poll: pollLogic,
  arena: arenaLogic,
  coop: coopLogic,
  sandbox: sandboxLogic,
  order: sequencerLogic,
  point: pinpointLogic,
  wall: wallLogic,
  completar: completarLogic,
  desarrollo: desarrolloLogic,
  vincula: vinculaLogic,
  agrupa: agrupaLogic,
  dictado: dictadoLogic,
  anatomia: anatomiaLogic,
  rhythm_canvas: rhythmCanvasLogic,
  melody_staff: melodyStaffLogic,
  ear_training: earTrainingLogic,
  info_block: infoBlockLogic,
  canvas_breakdown: canvasBreakdownLogic,
  symbol_analysis: symbolAnalysisLogic,
  geometry_canvas: geometryCanvasLogic,
  cartesian_plane: cartesianPlaneLogic,
  math_equation: mathEquationLogic,
  number_line: numberLineLogic,
  counting_collection: countingCollectionLogic,
  data_chart: dataChartLogic,
  fraction_model: fractionModelLogic,
  percent_grid: percentGridLogic,
  subtitulo: subtituloLogic,
  destacado: destacadoLogic,
  figura: figuraLogic,
  glosario_tabla: glosarioTablaLogic,
  formula_destacada: formulaDestacadaLogic,
  structural_breakdown: structuralBreakdownLogic,
  source_analysis: sourceAnalysisLogic,
};

export function getLogicPlugin<M extends GameMode>(mode: M): LogicPluginForMode<M> {
  const normalized = normalizeGameMode(mode) ?? (String(mode ?? '').trim() as GameMode);
  const plugin = logicRegistry[normalized];
  if (!plugin) {
    const available = Object.keys(logicRegistry).join(', ');
    throw new Error(`Logic plugin not registered: ${String(mode)}. Available: ${available}`);
  }
  return plugin as LogicPluginForMode<M>;
}

export function listLogicPlugins(): AnyLogicPlugin[] {
  return Object.values(logicRegistry);
}

export function registerLogicPlugin(plugin: AnyLogicPlugin): void {
  logicRegistry[plugin.mode] = plugin;
}

export function buildPluginContext<M extends GameMode>(
  mode: M,
  payload: ActivityPayloadMap[M],
  runtime: ActivityRuntimeMap[M],
  routing: RoutingMode,
  phase: string,
): PluginContext<M> {
  return { mode, payload, runtime, routing, phase };
}

export function validateActivityPayload<M extends GameMode>(
  mode: M,
  raw: unknown,
): ActivityPayloadMap[M] {
  const validated = getLogicPlugin(mode).validatePayload(raw);
  if (!supportsBlockStimulus(mode)) return validated;
  return applyStimulusFields(validated as Record<string, unknown>, raw, mode) as ActivityPayloadMap[M];
}

export function createActivityRuntime<M extends GameMode>(
  mode: M,
  payload: ActivityPayloadMap[M],
  routing: RoutingMode,
): ActivityRuntimeMap[M] {
  return getLogicPlugin(mode).createInitialRuntime(payload, routing);
}

/**
 * Punto de entrada del servidor para input de alumno.
 * El backend no conoce crucigramas ni mercados — solo delega al plugin.
 */
export function runStudentInput(params: {
  mode: GameMode;
  payload: unknown;
  runtime: unknown;
  routing: RoutingMode;
  phase: string;
  input: StudentInputEnvelope;
  participant: Participant;
}): PluginMutationResult {
  const plugin = getLogicPlugin(params.mode);
  const payload = plugin.validatePayload(params.payload);
  const ctx = buildPluginContext(
    params.mode,
    payload,
    params.runtime as ActivityRuntimeMap[typeof params.mode],
    params.routing,
    params.phase,
  );
  return plugin.onStudentInput(ctx, params.input, params.participant);
}

/**
 * Punto de entrada del servidor para acciones del host durante ACTIVE.
 */
export function runHostAction(params: {
  mode: GameMode;
  payload: unknown;
  runtime: unknown;
  routing: RoutingMode;
  phase: string;
  action: HostActionPayload;
}): PluginMutationResult {
  const plugin = getLogicPlugin(params.mode);
  const payload = plugin.validatePayload(params.payload);
  const ctx = buildPluginContext(
    params.mode,
    payload,
    params.runtime as ActivityRuntimeMap[typeof params.mode],
    params.routing,
    params.phase,
  );
  return plugin.onHostAction(ctx, params.action);
}

export function runComputeScores(params: {
  mode: GameMode;
  payload: unknown;
  runtime: unknown;
  routing: RoutingMode;
  phase: string;
  participants: Participant[];
}): ScorePatch[] {
  const plugin = getLogicPlugin(params.mode);
  if (!plugin.computeScores) return [];
  const payload = plugin.validatePayload(params.payload);
  const ctx = buildPluginContext(
    params.mode,
    payload,
    params.runtime as ActivityRuntimeMap[typeof params.mode],
    params.routing,
    params.phase,
  );
  return plugin.computeScores(ctx, params.participants);
}

export { logicRegistry };
